import { useReactiveVar } from '@apollo/client';

import { userStateVar } from 'core/state/userState';
import { UserRole } from 'types';

/**
 * Capability booleans for the survey surfaces, mirroring the backend's two
 * tiers (spec 0038). Hiding a control is a UX affordance only — the server
 * remains the authority on every query.
 *
 * The split is the point:
 *  - AGGREGATES carry no names and no free text, so HR is in: HR owns the
 *    conversation with a teacher and needs the numbers behind it.
 *  - NAMED DETAIL pairs a student with their verbatim opinion of a named
 *    teacher. Survey owners only.
 *
 * Getting this wrong is not cosmetic. Before it existed, HR opened the
 * comment feed and was told «комментариев нет» — a refusal displayed as a
 * fact about the data.
 */
export interface ISurveyPermissions {
  /** Admin, AccountManager, Methodist, HR — surveyAnalytics / surveySignals. */
  canReadAnalytics: boolean;
  /** Admin, AccountManager, Methodist — the survey constructor and its list. */
  canManageSurveys: boolean;
  /** Same set — the comment feed and a student's answers. */
  canReadNamedAnswers: boolean;
}

const hasAnyRole = (roles: UserRole[], allowed: UserRole[]): boolean =>
  roles.some((role) => allowed.includes(role));

/** ACCOUNTANT is this app's name for the backend's AccountManager. */
const SURVEY_MANAGER_ROLES = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.METHODIST,
];

const ANALYTICS_ROLES = [...SURVEY_MANAGER_ROLES, UserRole.HRMANAGER];

export const useSurveyPermissions = (): ISurveyPermissions => {
  const userState = useReactiveVar(userStateVar);
  const roles = (userState.me?.roles ?? []) as UserRole[];

  const canManageSurveys = hasAnyRole(roles, SURVEY_MANAGER_ROLES);

  return {
    canReadAnalytics: hasAnyRole(roles, ANALYTICS_ROLES),
    canManageSurveys,
    canReadNamedAnswers: canManageSurveys,
  };
};
